/**
 * Radio messages and phraseology helpers. Text is what the log shows; `speech` is what the
 * browser's speech synthesis says (digits spelt out, "niner", runway "two eight right").
 */
import { NATO, spokenCallsign, spokenDigits } from './airlines';
import type { Aircraft } from './aircraft';
import type { CrewStyle } from './phraseology';

export type RadioKind = 'command' | 'readback' | 'unable' | 'checkin' | 'report' | 'handoff' | 'system' | 'emergency';

export interface RadioMessage {
  id: number;
  timeS: number;
  from: 'atc' | 'pilot';
  callsign: string;
  text: string;
  speech: string;
  kind: RadioKind;
  /** Accent key for synthesized speech (sim/src/airlines.ts ACCENT_KEYS); absent means no direction. */
  accent?: string;
  /** The crew's way of talking (phraseology.ts CrewStyle), so a generated voice can deliver it that way. Pilots only. */
  crewStyle?: CrewStyle;
  /** The controller said this into the microphone, so the player has already heard it. */
  voiced?: boolean;
  /** What a controller transmission did to the flight, as the sim saw it; shown beside the line. */
  effect?: RadioEffect;
  /** Which brain chose a pilot's words, and why the rules spoke instead when another brain was refused. */
  brain?: { source: 'rules' | 'llm' | 'replay' | 'fallback'; reason?: string };
}

/**
 * `done`: an instruction the crew accepted. `talk`: a question or chat that changed nothing.
 * `refused`: the crew did not fly it (unable, or an order it could not make sense of).
 */
export interface RadioEffect { tone: 'done' | 'talk' | 'refused'; text: string }

export interface Phrase { text: string; speech: string }

export function altitudeText(ft: number): string {
  return ft >= 18000 ? `FL${Math.round(ft / 100)}` : ft.toLocaleString('en-US');
}
export function altitudeSpeech(ft: number): string {
  if (ft >= 18000) return `flight level ${spokenDigits(Math.round(ft / 100))}`;
  const thousands = Math.floor(ft / 1000), hundreds = Math.round((ft % 1000) / 100);
  let s = thousands ? `${spokenDigits(thousands)} thousand` : '';
  if (hundreds) s += ` ${spokenDigits(hundreds)} hundred`;
  return s.trim();
}
export function headingText(deg: number): string { return String(Math.round(deg) % 360 || 360).padStart(3, '0'); }
export function headingSpeech(deg: number): string { return spokenDigits(headingText(deg)); }
export function runwaySpeech(rw: string): string {
  const m = /^(\d{1,2})([LRC])?$/.exec(rw);
  if (!m) return rw;
  const side = m[2] === 'L' ? ' left' : m[2] === 'R' ? ' right' : m[2] === 'C' ? ' center' : '';
  return `${spokenDigits(m[1]!)}${side}`;
}
/** Navaid idents are spelt phonetically ("bravo golf november"); five-letter fixes are said as words. */
export function fixSpeech(fix: string): string { return fix.length <= 3 ? fix.split('').map((c) => NATO[c] ?? c).join(' ') : fix.toLowerCase(); }
export function speedSpeech(kt: number): string { return `${spokenDigits(kt)} knots`; }

export function callsignPhrase(ac: Aircraft, telephony?: Record<string, string>): Phrase {
  return { text: ac.callsign, speech: spokenCallsign(ac.callsign, ac.airline, telephony, ac.callsignForm) };
}

/** Join phrase parts; the callsign leads for ATC and trails for pilots. */
export function atcPhrase(cs: Phrase, body: Phrase): Phrase {
  return { text: `${cs.text}, ${body.text}`, speech: `${cs.speech}, ${body.speech}` };
}
export function pilotPhrase(body: Phrase, cs: Phrase): Phrase {
  return { text: `${cap(body.text)}, ${cs.text}`, speech: `${body.speech}, ${cs.speech}` };
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
